import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { User } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import SaveButton from './SaveButton';
import CancelButton from './CancelButton';
import { PasswordRules, passwordMeetsRules } from './SignInScreens';
import { useStaffAuth } from './StaffAuthContext';
import './RowActionsMenu.css';

// The signed-in staff member's own menu at the right of the topbar (issue
// #251): My Account (issue #259), Change password and Sign out. While the
// tenant is still on the shared app password there's nobody signed in, so
// it shows that instead.
const INPUT_STYLE = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, boxSizing: 'border-box' };

function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCurrent('');
    setNext('');
    setConfirm('');
    setError('');
    setDone(false);
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      setError('The two new passwords don\'t match.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await axios.post('/api/auth/change-password', { current_password: current, new_password: next });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not change your password');
    }
    setSaving(false);
  };

  return (
    <ConfirmDialog open={open} onOpenChange={onOpenChange} title="Change password">
      {done ? (
        <div style={{ fontSize: 14 }}>
          Your password has been changed.
          <div style={{ marginTop: 16 }}>
            <SaveButton type="button" onClick={() => onOpenChange(false)}>Close</SaveButton>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label style={{ fontSize: 12, color: '#5a6b85' }}>Current password</label>
            <input autoFocus type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} style={INPUT_STYLE} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#5a6b85' }}>New password</label>
            <input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} style={INPUT_STYLE} />
            <PasswordRules password={next} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#5a6b85' }}>Confirm new password</label>
            <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} style={INPUT_STYLE} />
          </div>
          {error && <div style={{ color: 'red', fontSize: 13 }}>{error}</div>}
          <div style={{ marginTop: 4 }}>
            <SaveButton loading={saving} disabled={!current || !passwordMeetsRules(next) || !confirm} />
            <CancelButton onClick={() => onOpenChange(false)} disabled={saving} style={{ marginLeft: 8 }} />
          </div>
        </form>
      )}
    </ConfirmDialog>
  );
}

function UserMenu() {
  const { currentUser, signOut } = useStaffAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (!currentUser) {
    return (
      <span
        className="nav-btn module-nav-btn"
        title="Opened with the shared app password. Invite staff from Global > Users so each person signs in as themselves."
        style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'default' }}
      >
        <User size={20} strokeWidth={1.5} />
        <span style={{ fontSize: 13 }}>Shared access</span>
      </span>
    );
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="nav-btn module-nav-btn"
        title={`Signed in as ${currentUser.email}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
      >
        <User size={20} strokeWidth={1.5} />
        <span style={{ fontSize: 13 }}>{currentUser.name}</span>
      </button>
      {open && (
        <div className="row-menu-dropdown" style={{ right: 0, color: '#1f2937' }}>
          <button type="button" className="row-menu-item" onClick={() => { setOpen(false); navigate('/global-my-account'); }}>
            My Account
          </button>
          <button type="button" className="row-menu-item" onClick={() => { setOpen(false); setChangingPassword(true); }}>
            Change password
          </button>
          <button type="button" className="row-menu-item" onClick={() => { setOpen(false); signOut(); }}>
            Sign out
          </button>
        </div>
      )}
      <ChangePasswordDialog open={changingPassword} onOpenChange={setChangingPassword} />
    </div>
  );
}

export default UserMenu;
